import React, { useState } from 'react';
import { Button, Paper, TextField, Typography, Box, InputAdornment, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { signInAsync } from '../../redux/slices/authSlice';
import useFormInput from '../../hooks/useFormInput';
import Logo from '../../components/Logo';

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const PageContainer = styled('div')({
  width: '100vw',
  minHeight: '100vh',
  padding: '0 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(-45deg, #1a1a2e, #16213e, #0f3460, #1a1a2e)',
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(80, 115, 248, 0.15) 0%, transparent 70%)',
    borderRadius: '50%',
    top: '-250px',
    left: '-250px',
    animation: `${float} 6s ease-in-out infinite`
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(255, 235, 59, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    bottom: '-200px',
    right: '-200px',
    animation: `${float} 8s ease-in-out infinite`
  }
});

const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: 480,
  width: '100%',
  padding: '48px 40px',
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(30, 30, 30, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
  }
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 32,
  '& > div': {
    transform: 'scale(1.2)'
  }
});

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: 8,
  background: 'linear-gradient(135deg, #FFEB3B 0%, #FFC107 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  marginBottom: 32,
  opacity: 0.8
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 20,
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '14px'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease'
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: 'rgba(255, 235, 59, 0.3)'
      }
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px'
      }
    }
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontSize: '15px'
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: 8,
  height: 52,
  fontSize: '16px',
  fontWeight: 700,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FFEB3B 0%, #FFC107 100%)',
  color: '#000000',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(255, 235, 59, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #FFC107 0%, #FFB300 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(255, 235, 59, 0.4)'
  },
  '&:active': {
    transform: 'translateY(0px)'
  },
  transition: 'all 0.3s ease'
}));

const SignInPage = () => {
  const dispatch = useDispatch();
  const login = useFormInput('');
  const password = useFormInput('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <PageContainer>
      <FormContainer elevation={24}>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        
        <WelcomeText>
          Вітаємо!
        </WelcomeText>
        <SubText>
          Увійдіть в адміністративну панель ECOS
        </SubText>

        <form
          onSubmit={event => {
            event.preventDefault();
            dispatch(
              signInAsync({ login: login.value, password: password.value })
            );
          }}
        >
          <StyledTextField
            {...login}
            fullWidth
            label='Логін'
            placeholder='Введіть ваш логін'
            autoComplete='username'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: 'rgba(255, 235, 59, 0.6)' }} />
                </InputAdornment>
              ),
            }}
          />
          
          <StyledTextField
            {...password}
            fullWidth
            label='Пароль'
            placeholder='Введіть ваш пароль'
            type={showPassword ? 'text' : 'password'}
            autoComplete='current-password'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'rgba(255, 235, 59, 0.6)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <SubmitButton
            fullWidth
            type='submit'
            variant='contained'
          >
            Увійти
          </SubmitButton>
        </form>
      </FormContainer>
    </PageContainer>
  );
};

export default SignInPage;
